import React, { useState } from 'react';
import { Icon } from '@growthaxl/web-foundation';

export interface DropZoneProps {
  enabled: boolean;
  onFilesDropped: (files: File[]) => void;
  children: React.ReactNode;
}

export const DropZone: React.FC<DropZoneProps> = ({ enabled, onFilesDropped, children }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enabled) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enabled) return;
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enabled) return;
    // Only clear highlight when leaving the dropzone entirely (not a child)
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!enabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFilesDropped(files);
    }
  };

  return (
    <div
      data-testid="esa-dropzone"
      className="relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}

      {isDragOver && (
        <div
          className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center gap-[6px] rounded-xl border border-dashed border-border-focus bg-brand-primary-soft/40"
          aria-live="polite"
          role="status"
        >
          <Icon name="upload" className="text-brand-secondary" />
          <span className="text-[11.5px] font-medium text-brand-secondary">Drop to attach</span>
        </div>
      )}
    </div>
  );
};
